// In-memory resolver for the DDC S3 gateway's `/auth/info` payload
// (gateway_public_key, cluster_id, region). Called once from the browser
// at app bootstrap (see src/config/runtime.ts) so the vault UI can sign
// delegation tokens and prompt proxy.addProxy without baking the gateway
// pubkey into env. Caches successful lookups for 5 minutes; failures do
// NOT cache so the next request retries instead of starving the UI for
// the rest of the TTL window.

const TTL_MS = 5 * 60 * 1000;
// The dev cluster's /auth/info regularly answers in ~2.2s, so the old 2s cap
// aborted the request ("cancelled") and left gatewayPublicKey null — which
// blocks vault claim with "Gateway public key unavailable". 10s tolerates the
// slow dev gateway while still bounding a genuinely dead endpoint.
const FETCH_TIMEOUT_MS = 10_000;

interface AuthInfo { gateway_public_key?: string; cluster_id?: string; region?: string }

export interface GatewayInfo {
  gatewayPublicKey: string | null;
  clusterId: string | null;
  region: string | null;
}

interface CachedEntry extends GatewayInfo {
  gatewayPublicKey: string;
  at: number;
}

let cache: CachedEntry | null = null;

export function resetGatewayInfoCacheForTests(): void {
  cache = null;
}

async function fetchAuthInfo(
  s3Endpoint: string,
  fetchImpl: typeof fetch,
): Promise<Omit<CachedEntry, 'at'> | null> {
  try {
    const r = await fetchImpl(s3Endpoint.replace(/\/+$/, '') + '/auth/info', {
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    if (!r.ok) return null;
    const info = (await r.json()) as AuthInfo;
    if (!info.gateway_public_key) return null;
    return {
      gatewayPublicKey: info.gateway_public_key,
      clusterId: info.cluster_id ?? null,
      region: info.region ?? null,
    };
  } catch {
    return null;
  }
}

export async function getGatewayInfo(
  s3Endpoint: string | undefined,
  fetchImpl: typeof fetch = fetch,
  now: () => number = Date.now,
): Promise<GatewayInfo> {
  const empty: GatewayInfo = { gatewayPublicKey: null, clusterId: null, region: null };
  if (!s3Endpoint) return empty;
  if (cache && now() - cache.at < TTL_MS) {
    return { gatewayPublicKey: cache.gatewayPublicKey, clusterId: cache.clusterId, region: cache.region };
  }

  const fresh = await fetchAuthInfo(s3Endpoint, fetchImpl);
  if (fresh) {
    cache = { ...fresh, at: now() };
    return { gatewayPublicKey: fresh.gatewayPublicKey, clusterId: fresh.clusterId, region: fresh.region };
  }
  // Stale-while-error: a failed refresh should not erase a previously good
  // value. Keep the existing cache entry (even if past TTL) so the UI can
  // keep operating; the next request will retry the upstream fetch.
  if (cache) {
    return { gatewayPublicKey: cache.gatewayPublicKey, clusterId: cache.clusterId, region: cache.region };
  }
  return empty;
}
